import {
  AlignItems,
  Box,
  Breakpoint,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  Display2,
  DisplayPosition,
  FlexDirection,
  FontWeight,
  Icon,
  IconVariant,
  JustifyContent,
  Text,
  withCitrusTheme,
  ZIndex,
} from '@gousto-internal/citrus-react'
import React from 'react'

import type { OrderBoxData } from '@library/api-customer-engagement-experience'

import platesDesktop from './assets/plates-desktop.webp'
import platesMobile from './assets/plates-mobile.webp'
import platesTablet from './assets/plates-tablet.webp'

type OrderAnotherBoxProps = {
  data: OrderBoxData
  onAction: (intent: string) => void
}

const CARD_RADIUS = '8px'

// Opacity of the scrim behind copy overlapping the plates. Higher = more contrast.
const SCRIM_OPACITY = 0.9

// Card-coloured scrim behind copy for legibility over the plates; `inset` tunes its extent.
const scrimBefore = (backgroundColor: string | undefined, inset: string | number) => ({
  content: '""',
  position: 'absolute' as const,
  inset,
  borderRadius: CARD_RADIUS,
  backgroundColor,
  opacity: SCRIM_OPACITY,
  zIndex: -1,
})

const Container = withCitrusTheme(Box, (theme) => ({
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: theme.color(Color.ColdGrey_800),
  borderRadius: CARD_RADIUS,
  maxWidth: '1200px',
  margin: '0 auto',
}))

// Decorative plate cut-outs pinned to the card's right edge, clipped by the Container.
const PlatesImage = withCitrusTheme('img', () => ({
  position: 'absolute',
  top: 0,
  right: 0,
  height: '100%',
  width: 'auto',
  pointerEvents: 'none',
  userSelect: 'none',
}))

// Kept in the title row (not absolute) so a long title wraps beside it, not under it.
const DiscountBadge = withCitrusTheme(Box, () => ({
  borderRadius: '4px',
  whiteSpace: 'nowrap',
  flexShrink: 0,
}))

// Sentence-cased on mobile; uppercase from Small up.
const Heading = withCitrusTheme(Display2, (theme) => ({
  position: 'relative',
  alignSelf: 'flex-start',
  minWidth: 0, // shrink and wrap beside the badge instead of overflowing
  margin: 0,
  textTransform: 'none',
  '&::before': scrimBefore(theme.color(Color.ColdGrey_800), '-2px -8px'),
  [`@media (min-width: ${Breakpoint.Small})`]: {
    textTransform: 'uppercase',
  },
}))

// Scrim (::before) behind the tag list, hugging the tags so they're inset within the panel.
const TagList = withCitrusTheme('ul', (theme) => ({
  position: 'relative',
  alignSelf: 'flex-start',
  listStyle: 'none',
  margin: 0,
  // Tighter vertical padding on mobile; full inset from Small up.
  padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
  [`@media (min-width: ${Breakpoint.Small})`]: {
    padding: `${theme.spacing(2)}px`,
  },
  display: 'flex',
  flexDirection: 'column',
  gap: `${theme.spacing(2)}px`,
  color: theme.color(Color.White),
  '&::before': scrimBefore(theme.color(Color.ColdGrey_800), 0),
}))

// Tick inherits Success_50 (colour/icon/success); the label overrides back to white.
const TagItem = withCitrusTheme('li', (theme) => ({
  alignItems: 'center',
  display: 'flex',
  gap: `${theme.spacing(2)}px`,
  color: theme.color(Color.Success_50),
}))

export const OrderAnotherBox = ({ data, onAction }: OrderAnotherBoxProps) => (
  <Container data-testid="order-another-box">
    <picture>
      <source media={`(min-width: ${Breakpoint.Large})`} srcSet={platesDesktop} />
      <source media={`(min-width: ${Breakpoint.Medium})`} srcSet={platesTablet} />
      <PlatesImage alt="" aria-hidden="true" src={platesMobile} />
    </picture>

    <Box
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={3}
      paddingH={4}
      paddingV={4}
      position={DisplayPosition.Relative}
      zIndex={ZIndex.Default}
    >
      {/* gap={4} accounts for the heading scrim's 8px bleed, leaving space before the badge */}
      <Box
        alignItems={AlignItems.FlexStart}
        display={Display.Flex}
        gap={4}
        justifyContent={JustifyContent.SpaceBetween}
      >
        <Heading color={Color.White} size={[7, 13]}>
          {data.title}
        </Heading>

        {data.offer ? (
          <DiscountBadge bg={Color.Success_900} color={Color.White} paddingH={2} paddingV={1}>
            <Text fontWeight={FontWeight.Bold} size={[3, 5, 6]}>
              {data.offer.badge}
            </Text>
          </DiscountBadge>
        ) : null}
      </Box>

      <TagList>
        {data.tags.map((tag) => (
          <TagItem key={tag}>
            <Icon aria-hidden="true" name="tick" size={6} variant={IconVariant.Inherit} />
            <Text color={Color.White} fontWeight={FontWeight.Normal} size={[4, 5]}>
              {tag}
            </Text>
          </TagItem>
        ))}
      </TagList>

      <Box
        alignItems={AlignItems.FlexStart}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={3}
      >
        <Button
          colorVariant={ButtonColorVariant.Primary}
          data-testid="order-another-box-cta"
          height={48}
          onClick={() => onAction(data.cta.intent)}
          type="button"
          width={['100%', null, null, 440]}
        >
          {data.cta.title}
        </Button>

        {data.offer?.expiry_label ? (
          <Box alignItems={AlignItems.Center} color={Color.White} display={Display.Flex} gap={1}>
            <Icon aria-hidden="true" name="info" size={4} variant={IconVariant.Inherit} />
            <Text color={Color.White} size={[1, 2]}>
              {data.offer.expiry_label}
            </Text>
          </Box>
        ) : null}
      </Box>
    </Box>
  </Container>
)
